// src/modules/app-views/account/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import SubscriptionsDocument from './pages/Subscription';
import AccountInit from './pages/Init';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import useZustand from 'hooks/useZustand';
import subscriptionActionButton from 'modules/app-views/account/pages/Subscription/actions/actionButton';
import initActionButton from 'modules/app-views/account/pages/Init/actions/actionButton';
import initDecorateResponse from 'modules/app-views/account/pages/Init/decorators/response';
import initDecorateRequest from 'modules/app-views/account/pages/Init/decorators/request';
import subscriptionDecorateResponse from 'modules/app-views/account/pages/Subscription/decorators/response';
import subscriptionDecorateRequest from 'modules/app-views/account/pages/Subscription/decorators/request';
import { API } from 'constants/Api';
import CardHeaderCountryIcon from 'layouts/Pages/PageHeader/CardHeaderCountryIcon';
import CardHeaderTeamMembers from 'layouts/Pages/PageHeader/CardHeaderTeamMembers';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import {
  documentCodeItem,
  createdAtItem,
  updatedAtItem,
  emailItem,
  phoneItem,
  locationItem,
  cpfCnpjItem,
  genderSegmentItem,
  ageFoundedItem,
  companyNameItem,
  cnpjItem,
  nextDueDateItem,
  paymentMethodItem,
  subscriptionValueItem,
} from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import EntityProfilesTag from 'components/core/EntityProfilesTag';
import translateX from 'utils/translateX';
import app from 'configs/appResolver';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'account';
// export const ACCOUNTS_ROUTE = `/app/account/settings`;

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  api: null,
  // parametersModalComponent: null,
  // parametersDecorator: null,
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    highSecurity: true, // accounts dont have _metadata, this avoid MyProfile to access data from another user._id

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'page', // 'page' | 'list' | 'document'
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props} hideBackToHomeLink={true}>
        <AccountInit />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.ACCOUNTS.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: false,
      fetchWithoutSubscription: true,
      removeSubscriptionIdFromHeader: true,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: initDecorateRequest,
      response: initDecorateResponse,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:userId',
      paramKey: 'userId',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // SECURITY & ACCESS
    // ───────────────────────────────────────────────────────────────────────
    security: {
      requireSubscription: false,
    },

    // ───────────────────────────────────────────────────────────────────────
    // PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'my_profile',
      initialValues: {},
      actions: ['editRefresh', initActionButton()],
      viewMode: 'auto',
    },

    // settings: {
    //   initialTab: 'my_profile',
    //   initialValues: {},
    //   actions: ['new', 'add', 'view', 'edit', 'delete', 'archive', 'save'],
    //   viewMode: 'auto',
    // },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default', // 'default' | 'card'

      avatar: {
        visible: true,
        placeholder: 'photo',
        noShowInitials: false,
        noShowColor: false,
        hideToolbar: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: false,
      },

      buttons: {
        filter: false,
        refresh: false,
        goBack: false,
        parameters: false,
      },

      stats: {
        showTotalRecords: false,
      },

      // Only for style: 'card'
      card: {
        title: null,
        subtitle: null,
        column1: null,
        column2: null,
        column3: null,
        tags: null,
        icons: null,
        footer: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // TABS CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    tabs: {
      my_profile: {
        // actions: ['editRefresh', initActionButton()],

        card: {
          title: props => <CardHeaderTitle {...props} />,
          subtitle: () => <EntityProfilesTag tags={['user']} />,
          column1: props => (
            <CardHeaderColumn {...props} items={[documentCodeItem, createdAtItem, updatedAtItem]} />
          ),
          column2: props => (
            <CardHeaderColumn {...props} items={[emailItem, phoneItem, locationItem]} />
          ),
          column3: props => (
            <CardHeaderColumn {...props} items={[cpfCnpjItem, genderSegmentItem, ageFoundedItem]} />
          ),
          icons: (data, countryCode) => (
            <CardHeaderCountryIcon record={data} countryCode={countryCode} />
          ),
        },
      },

      team: {
        // Table bootstrap configuration for ListProviderV2
        table: {
          pagination: {
            page: 1,
            size: 10,
          },
          sort: {
            field: '_metadata.audit.createdAt',
            order: 'desc',
          },
          drawer: {
            folder: 'inbox',
          },
        },
      },

      subscriptions: {
        // No table configuration needed - uses TileItem cards
      },

      resources: {
        // No table configuration needed - uses DataDisplayWidget
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // CUSTOM COMPONENTS
    // ───────────────────────────────────────────────────────────────────────
    // components: {},

    // ───────────────────────────────────────────────────────────────────────
    // LIST SETTINGS (only for layout: 'list')
    // ───────────────────────────────────────────────────────────────────────
    // list: {
    //   table: {
    //     showLineNumbers: false,
    //     pagination: {
    //       initialPage: 1,
    //       pageSize: 100,
    //     },
    //     sorter: {
    //       field: 'name',
    //       order: 'asc', // 'asc' | 'desc'
    //     },
    //   },
    //
    //   header: {
    //     showFolder: false,
    //     showRecordCount: false,
    //   },
    //
    //   actions: {
    //     columns: ['new'],
    //   },
    //
    //   preview: {
    //     title: 'edit_user',
    //     showCard: false,
    //     header: null,
    //     body: null,
    //     actions: null,
    //   },
    //
    //   filter: {
    //     component: null,
    //   },
    //
    //   behavior: {
    //     editGoToPage: 'document',
    //   },
    // },
  },

  subscription: {
    highSecurity: true, // Verify userId in URL params matches storedUser._id

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <SubscriptionsDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.SUBSCRIPTIONS.URL,
      params: null,
      query: { profile: 'owner' },

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: true,
      removeSubscriptionIdFromHeader: true,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: subscriptionDecorateRequest,
      response: subscriptionDecorateResponse,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:userId/subscription/:subscriptionId',
      paramKey: 'subscriptionId',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // SECURITY & ACCESS
    // ───────────────────────────────────────────────────────────────────────
    security: {
      requireSubscription: false,
    },

    // ───────────────────────────────────────────────────────────────────────
    // PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {},
      actions: ['editRefresh', subscriptionActionButton(), 'return'],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card', // 'default' | 'card'

      // visible: false,
      // style: 'default', // 'default' | 'card'

      avatar: {
        visible: true,
        placeholder: 'company_logo',
      },

      navigation: {
        showTitle: true,
        // showBreadcrumb: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['organizations', 'record'],

        // showBreadcrumb: ({ data, moduleCtx }) => {
        //   if (moduleCtx?.mode === 'add') {
        //     return { add: ['subscriptions', 'new_subscription'], remove: 'add' };
        //   } else {
        //     return { add: ['subscriptions', data?.name], remove: [data?._id, 'new_subscription'] };
        //   }
        // },
      },

      // buttons: {
      //   filter: false,
      //   refresh: true,
      //   goBack: true,
      //   parameters: false,
      // },

      stats: {
        showTotalRecords: false,
      },

      card: {
        title: () => {
          const currentPage = useZustand?.('module', 'currentPage', false);
          const data = currentPage?.data;

          if (!data?._id) return <span>{translateX('new_organization')}</span>;

          const props = {
            name: data?.organization,
            tooltip: 'organization_name',
            twoClickToEdit: false,
            fieldName: 'organization',
          };
          return <CardHeaderTitle {...props} />;
        },

        subtitle: () => {
          const currentPage = useZustand?.('module', 'currentPage', false);
          const data = currentPage?.data;
          return <span className="text-muted">{translateX(data?.profile)}</span>;
        },

        column1: props => (
          <CardHeaderColumn {...props} items={[documentCodeItem, createdAtItem, updatedAtItem]} />
        ),

        column2: props => (
          <CardHeaderColumn
            {...props}
            items={[
              record => nextDueDateItem(record, app.PRESET?.CONTRACT),
              record => paymentMethodItem(record, app.PRESET?.CONTRACT),
              record => subscriptionValueItem(record, app.PRESET?.CONTRACT),
            ]}
          />
        ),

        column3: props => (
          <CardHeaderColumn {...props} items={[companyNameItem, cnpjItem, locationItem]} />
        ),

        icons: () => {
          const storedUser = useZustand?.('auth', 'user', false);
          const currentPage = useZustand?.('module', 'currentPage', false);
          const subscriptionId = currentPage?.data?._id;

          // Get team from storedUser.subscriptions (already populated with user data)
          const subscription = storedUser?.subscriptions?.find(s => s._id === subscriptionId);
          const team = subscription?.team;

          return <CardHeaderTeamMembers team={team} />;
        },
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // CUSTOM COMPONENTS
    // ───────────────────────────────────────────────────────────────────────
    components: {},
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  init: {
    dataOrigin: 'new_data',
    entityType: 'person',
    name: 'Rick',
    fullName: 'Rick Biruel',
    birthdate: '1979-08-17T03:00:00.000Z',
    gender: 'male',
    cpf: {
      value: '28100151806',
      countryCode: 'BR',
    },
    address: {
      zip: '13023-185',
      addressTitle: 'Endereço Principal',
      address1: 'Rua Sacramento',
      address2: 'Apto 92',
      neighborhood: 'Vila Itapura',
      city: 'Campinas',
      region: 'SP',
      __city: 'Campinas - SP',
      addressAdditionalInfo: {
        searchApi: 'viacep',
        areaCode: '19',
        giaCode: '2446',
        ibgeCode: '3509502',
        siafiCode: '6291',
        stateName: 'São Paulo',
        regionName: 'Sudeste',
      },
      addressMetadata: {
        countryCode: 'BR',
        foundByApi: true,
        apiUrl: 'https://viacep.com.br/ws/13023185/json/',
        zipWithMultipleAddress: false,
        manual: false,
        apiFailure: false,
      },
      addressCoordinates: [-22.898263, -47.0617444],
      number: '750',
    },
    email: {
      value: 'biruel@infodesk.com',
      _id: '6924c032b12f0f48f9326a30',
    },
    phone: {
      value: '19982320002',
      _id: '6924c032b12f0f48f9326a31',
      countryCode: 'BR',
    },
  },

  subscription: {
    entityType: 'company',
    dataOrigin: 'new_data',
    countryCode: 'BR',
    phone: {
      countryCode: 'BR',
      value: '(19) 3.2324-040',
      phone: '1932324040',
      dialCode: '+55',
      fullNumber: '+551932324040',
      whatsappNumber: '551932324040',
      national: '(19) 3232-4040',
      international: '+55 19 3232 4040',
      masked: '(19) *****4040',
      areaCode: '19',
      isMobile: false,
      isLandline: false,
      isValid: true,
      number: '32324040',
    },
    address: {
      countryCode: 'BR',
      zip: '13010-1111',
      number: '22',
      addressTitle: 'Endereço Principal',
      address1: 'Rua General Osório',
      address2: 'Sala 15',
      neighborhood: 'Centro',
      city: 'Campinas',
      region: 'SP',
      __city: 'Campinas - SP',
      addressAdditionalInfo: {
        searchApi: 'viacep',
        note: '',
        areaCode: '19',
        giaCode: '2446',
        ibgeCode: '3509502',
        siafiCode: '6291',
      },
      addressMetadata: {
        countryCode: 'BR',
        foundByApi: true,
        apiUrl: 'https://viacep.com.br/ws/13010111/json/',
        zipWithMultipleAddress: false,
        manual: false,
        apiFailure: false,
      },
      addressCoordinates: [-22.9075188, -47.0683532],
    },
    cnpj: { value: '02.934.311/0001-53' },
    name: 'Infodesk',
    fullName: 'Infodesk Technologies',
    email: { value: 'biruel@info.com' },
    foundedAt: '1999-01-05T02:00:00.000Z',
    segment: 'information_communication',
    backColor: '#02ef12',
    foreColor: '#ffffff',
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };

// *******************************************************************************
// HELPERS
// *******************************************************************************

const a = {
  cnpj_raiz: '02934311',
  razao_social: 'INFODESK TECHNOLOGIES LTDA',
  capital_social: '10000.00',
  responsavel_federativo: '',
  atualizado_em: '2026-01-10T03:00:00.000Z',
  porte: {
    id: '03',
    descricao: 'Empresa de Pequeno Porte',
  },
  natureza_juridica: {
    id: '2062',
    descricao: 'Sociedade Empresária Limitada',
  },
  qualificacao_do_responsavel: {
    id: 49,
    descricao: 'Sócio-Administrador ',
  },
  socios: [
    {
      cpf_cnpj_socio: '***001518**',
      nome: 'RICARDO BIRUEL',
      tipo: 'Pessoa Física',
      data_entrada: '1999-01-05',
      cpf_representante_legal: '***000000**',
      nome_representante: null,
      faixa_etaria: '41 a 50 anos',
      atualizado_em: '2025-12-13T03:00:00.000Z',
      pais_id: '1058',
      qualificacao_socio: {
        id: 49,
        descricao: 'Sócio-Administrador ',
      },
      qualificacao_representante: null,
      pais: {
        id: '1058',
        iso2: 'BR',
        iso3: 'BRA',
        nome: 'Brasil',
        comex_id: '105',
      },
    },
    {
      cpf_cnpj_socio: '***216268**',
      nome: 'CARLOS ROBERTO TIBA',
      tipo: 'Pessoa Física',
      data_entrada: '2015-12-21',
      cpf_representante_legal: '***000000**',
      nome_representante: null,
      faixa_etaria: '71 a 80 anos',
      atualizado_em: '2025-12-13T03:00:00.000Z',
      pais_id: '1058',
      qualificacao_socio: {
        id: 22,
        descricao: 'Sócio',
      },
      qualificacao_representante: null,
      pais: {
        id: '1058',
        iso2: 'BR',
        iso3: 'BRA',
        nome: 'Brasil',
        comex_id: '105',
      },
    },
  ],
  simples: {
    mei: 'Não',
    simples: 'Não',
    data_opcao_mei: null,
    data_exclusao_mei: null,
    data_opcao_simples: '2012-01-01',
    data_exclusao_simples: '2018-12-31',
    atualizado_em: '2026-01-10T03:00:00.000Z',
  },
  estabelecimento: {
    cnpj: '02934311000153',
    atividades_secundarias: [
      {
        id: '4751201',
        secao: 'G',
        divisao: '47',
        grupo: '47.5',
        classe: '47.51-2',
        subclasse: '4751-2/01',
        descricao: 'Comércio varejista especializado de equipamentos e suprimentos de informática',
      },
      {
        id: '6209100',
        secao: 'J',
        divisao: '62',
        grupo: '62.0',
        classe: '62.09-1',
        subclasse: '6209-1/00',
        descricao: 'Suporte técnico, manutenção e outros serviços em tecnologia da informação',
      },
    ],
    cnpj_raiz: '02934311',
    cnpj_ordem: '0001',
    cnpj_digito_verificador: '53',
    tipo: 'Matriz',
    nome_fantasia: 'INFODESK',
    situacao_cadastral: 'Baixada',
    data_situacao_cadastral: '2021-03-29',
    data_inicio_atividade: '1999-01-05',
    nome_cidade_exterior: null,
    tipo_logradouro: 'AVENIDA',
    logradouro: 'MARECHAL RONDON',
    numero: '700',
    complemento: 'SALA  709',
    bairro: 'JARDIM CHAPADAO',
    cep: '13070173',
    ddd1: '19',
    telefone1: '40428666',
    ddd2: '11',
    telefone2: '30320666',
    ddd_fax: '19',
    fax: '32945454',
    email: 'financeiro@infodesk.com.br',
    situacao_especial: null,
    data_situacao_especial: null,
    atualizado_em: '2026-01-10T03:00:00.000Z',
    atividade_principal: {
      id: '6201501',
      secao: 'J',
      divisao: '62',
      grupo: '62.0',
      classe: '62.01-5',
      subclasse: '6201-5/01',
      descricao: 'Desenvolvimento de programas de computador sob encomenda',
    },
    pais: {
      id: '1058',
      iso2: 'BR',
      iso3: 'BRA',
      nome: 'Brasil',
      comex_id: '105',
    },
    estado: {
      id: 26,
      nome: 'São Paulo',
      sigla: 'SP',
      ibge_id: 35,
    },
    cidade: {
      id: 3376,
      nome: 'Campinas',
      ibge_id: 3509502,
      siafi_id: '6291',
    },
    motivo_situacao_cadastral: null,
    inscricoes_estaduais: [
      {
        inscricao_estadual: '795506701117',
        ativo: false,
        atualizado_em: '2026-01-28T00:00:00.000Z',
        estado: {
          id: 26,
          nome: 'São Paulo',
          sigla: 'SP',
          ibge_id: 35,
        },
      },
    ],
  },
};
